import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NutritionDetailsRow } from '@/api/reports';
import type { Meal } from '@/api/meals';
import {
  MEAL_PERIODS,
  TIMING_NUTRIENTS,
  nutrientByPeriod,
  type PeriodDatum,
  type TimingNutrient,
} from '@/lib/nutrientDistribution';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/utils';
import { PercentBar } from './PercentBar';

type Series = { nutrient: TimingNutrient; periods: PeriodDatum[]; total: number };

/**
 * "Nutrient distribution by time of day" — the report's Calorie/Carbohydrate
 * distribution graph, generalized to chart up to three nutrients (chosen via
 * the in-card selector or Profile settings). A single nutrient renders the
 * report's full-width bars; multiple nutrients render side-by-side columns that
 * share the period labels, so they can be compared period-by-period.
 */
export const NutrientTimingChart = ({
  meals,
  rows,
  isLoading,
  nutrientKeys,
}: {
  meals: Meal[];
  rows: NutritionDetailsRow[];
  isLoading: boolean;
  nutrientKeys: string[];
}) => {
  const { t } = useTranslation();

  const nutrients = useMemo(
    () =>
      nutrientKeys
        .map((k) => TIMING_NUTRIENTS.find((n) => n.key === k))
        .filter((n): n is TimingNutrient => Boolean(n)),
    [nutrientKeys],
  );

  const series: Series[] = useMemo(
    () => nutrients.map((nutrient) => ({ nutrient, ...nutrientByPeriod(meals, rows, nutrient.field) })),
    [nutrients, meals, rows],
  );

  if (isLoading) return <LoadingState label={t('Loading…')} />;
  if (nutrients.length === 0) {
    return (
      <EmptyState
        title={t('No nutrients selected')}
        message={t('Choose up to 3 nutrients under Display preferences on your profile.')}
      />
    );
  }

  return series.length === 1 ? (
    <SingleNutrient series={series[0]} />
  ) : (
    <GroupedNutrients series={series} />
  );
};

/** Report-faithful single-nutrient view: full-width bars with the daily average. */
const SingleNutrient = ({ series }: { series: Series }) => {
  const { t } = useTranslation();
  const { nutrient, periods, total } = series;

  if (total <= 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('No {{nutrient}} logged in this date range.', { nutrient: t(nutrient.label).toLowerCase() })}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {periods.map((p) => {
        const period = MEAL_PERIODS.find((mp) => mp.key === p.key)!;
        return (
          <PercentBar
            key={p.key}
            label={t(period.label)}
            sublabel={t(period.time)}
            fraction={p.pct}
            colour={nutrient.colour}
            rightText={t('{{value}} avg. {{unit}}', { value: Math.round(p.avg), unit: nutrient.unit })}
          />
        );
      })}
    </div>
  );
};

/**
 * Side-by-side comparison: period labels on the left, one squished column per
 * nutrient. Rows align across nutrients so a period reads left-to-right.
 */
const GroupedNutrients = ({ series }: { series: Series[] }) => {
  const { t } = useTranslation();
  const cols = `minmax(4.5rem, auto) repeat(${series.length}, minmax(0, 1fr))`;

  return (
    <div className="grid items-center gap-x-3 gap-y-2.5" style={{ gridTemplateColumns: cols }}>
      {/* Header: nutrient name + colour swatch above each column. */}
      <div />
      {series.map((s) => (
        <div key={s.nutrient.key} className="flex min-w-0 items-center gap-1.5">
          <span aria-hidden className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: s.nutrient.colour }} />
          <span className="truncate text-xs font-semibold">{t(s.nutrient.label)}</span>
        </div>
      ))}

      {MEAL_PERIODS.map((period) => (
        <GroupedRow key={period.key} periodKey={period.key} series={series} />
      ))}
    </div>
  );
};

const GroupedRow = ({
  periodKey,
  series,
}: {
  periodKey: PeriodDatum['key'];
  series: Series[];
}) => {
  const { t } = useTranslation();
  const period = MEAL_PERIODS.find((mp) => mp.key === periodKey)!;

  return (
    <>
      <div className="min-w-0">
        <div className="truncate text-xs font-semibold leading-tight">{t(period.label)}</div>
        <div className="text-[0.5625rem] leading-tight text-muted-foreground">{t(period.time)}</div>
      </div>
      {series.map((s) => {
        const datum = s.periods.find((p) => p.key === periodKey)!;
        return (
          <MiniBar
            key={s.nutrient.key}
            fraction={datum.pct}
            colour={s.nutrient.colour}
            title={t('{{value}} avg. {{unit}}', { value: Math.round(datum.avg), unit: s.nutrient.unit })}
          />
        );
      })}
    </>
  );
};

/** A compact bar for the grouped view: fill + percent below, average on hover. */
const MiniBar = ({
  fraction,
  colour,
  title,
  className,
}: {
  fraction: number;
  colour: string;
  title: string;
  className?: string;
}) => {
  const pct = Math.max(0, Math.min(1, fraction));
  return (
    <div className={cn('min-w-0', className)} title={title}>
      <div className="relative h-5 overflow-hidden rounded-sm" style={{ backgroundColor: `${colour}1A` }}>
        <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${pct * 100}%`, backgroundColor: colour }} />
      </div>
      <div className="mt-0.5 text-center text-[0.625rem] font-semibold tabular-nums">{(pct * 100).toFixed(1)}%</div>
    </div>
  );
};
